import { useState, useEffect } from 'react';
import axios from 'axios';
import type { ActivityLog } from './types';
import { ShieldCheck, History, User, Calendar } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";

function SecurityPage() {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => { fetchActivityLogs(); }, [page]);

  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/activity-log`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 50 }
      });
      setActivityLogs(response.data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes('login')) return { bg: 'bg-green/15', text: 'text-green' };
    if (action.includes('delete') || action.includes('ban')) return { bg: 'bg-red/15', text: 'text-red' };
    if (action.includes('view')) return { bg: 'bg-primary/15', text: 'text-primary' };
    if (action.includes('update') || action.includes('change')) return { bg: 'bg-orange/15', text: 'text-orange' };
    return { bg: 'bg-white/10', text: 'text-white/50' };
  };

  const formatAction = (action: string) =>
    action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const stats = [
    { icon: <History size={18} className="text-primary" />, label: 'Total Events', value: activityLogs.length },
    { icon: <User size={18} className="text-green" />, label: 'Active Admins', value: new Set(activityLogs.map(l => l.adminId?.email)).size },
    { icon: <Calendar size={18} className="text-orange" />, label: "Today's Activity", value: activityLogs.filter(l => new Date(l.createdAt).toDateString() === new Date().toDateString()).length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
          <ShieldCheck size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Security & Activity Log</h1>
          <p className="text-white/40 text-sm mt-0.5">Monitor all administrative actions and system events</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <div key={i} className={`${CARD} p-5 flex items-center gap-3`}>
            <div className="w-9 h-9 rounded-lg bg-white/[0.05] flex items-center justify-center flex-shrink-0">
              {s.icon}
            </div>
            <div>
              <p className="text-white/40 text-xs">{s.label}</p>
              <p className="text-2xl font-bold text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className={CARD}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white">Recent Activity</h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {['Timestamp', 'Admin', 'Action', 'Target', 'IP Address', 'Details'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log) => {
                  const { bg, text } = getActionColor(log.action);
                  return (
                    <tr key={log._id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-white/50 text-xs whitespace-nowrap">{formatDate(log.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {log.adminId?.name?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <p className="text-white/80 text-xs font-medium">{log.adminId?.name || 'Unknown'}</p>
                            <p className="text-white/30 text-xs">{log.adminId?.email || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${bg} ${text}`}>
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/50 text-xs">
                        {log.targetType ? (
                          <span>{log.targetType} {log.targetId && <code className="bg-white/[0.06] px-1 rounded">{log.targetId.slice(0, 8)}…</code>}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-white/40 text-xs bg-white/[0.04] px-1.5 py-0.5 rounded">{log.ipAddress || 'N/A'}</code>
                      </td>
                      <td className="px-4 py-3">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-primary/70 hover:text-primary transition-colors">View Details</summary>
                            <pre className="mt-2 text-white/40 bg-white/[0.04] p-2 rounded text-[10px] max-h-32 overflow-auto">{JSON.stringify(log.details, null, 2)}</pre>
                          </details>
                        ) : <span className="text-white/20">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-white/[0.06]">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="text-sm text-white/50 hover:text-white disabled:opacity-30 transition-colors">
            ← Previous
          </button>
          <span className="text-white/30 text-sm">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={activityLogs.length < 50}
            className="text-sm text-white/50 hover:text-white disabled:opacity-30 transition-colors">
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

export default SecurityPage;
